import {
  ChangeEvent,
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { Dialog, Transition } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import instance from '../config/axios';
import { ProfileDisplay } from '../components/ProfileDisplay';
import {
  AlertIcon,
  DeactivateIcon,
  EditIcon,
  MobileCancelIcon,
  MobileSaveIcon,
  ProfileEmailIcon,
  ProfileLocationIcon,
  ProfileMobileIcon,
  ProfileTelePhoneIcon,
  WalletIcon,
} from '../assets/svgImages';
import { getUserInfo, manageNotification, updatePatientData } from '../service';
import {
  Emergencycontact,
  NotificationOptions,
  ROLE,
  UserInfoResponse,
} from '../interface/interface';
import Loading from '../components/Loading';
import moment from 'moment';
import showToast from '../components/ToastComponent';
import SkeletonLoader from '../components/SkeletonLoader';
import { formatPhoneNumber } from '../utils/formatPhoneNumber';
import { Tooltip } from '../components/Tooltip';
import { formatMedicareNumber } from '../utils/formatUtils';
import CustomModal from '../components/CustomModal';
import { UserContext } from '../context/userContext';
import { WrapEmail } from '../components/WrapEmail';
import { getPasswordResetUrl } from '../service/getPasswordUrl';

export const ProfileComponent = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [userInfo, setUserInfo] = useState<UserInfoResponse>();
  const [loading, setUserLoading] = useState<boolean>(false);
  const [isNotificationsEnabled, setNotificationStatus] = useState<boolean>();
  const [triggerUserDataFetch, setTrigger] = useState<boolean>(true);
  const [credits, setStoreCredits] = useState<number>(0);
  const [editable, setEditable] = useState(false);
  const [emergencyContacts, setEmergencyContact] = useState<Emergencycontact>();
  const [mailAlert, setMailAlert] = useState<boolean>(false);
  const [editMailModal, setEditMailModal] = useState<boolean>(false);
  const [isDisabled, setDisabled] = useState<boolean>(true);
  const { userDetail, refreshUserData, setLoadingEnable, setActiveItem } =
    useContext(UserContext);
  const [emailValidationMsg, setEmailValidationMsg] = useState('');
  const [updateRecord, setUpdateRecord] = useState({
    email: '',
    mobile: '',
    home: '',
    firstName: '',
    lastName: '',
  });
  const [isPushNotificationsEnabled, setPushNotificationStatus] =
    useState<boolean>();
  const [notificationType, setNotificationType] = useState<string | null>();

  useEffect(() => {
    setActiveItem('');
    if (userInfo?.user) {
      setUpdateRecord({
        email: userInfo?.user?.email ?? '',
        mobile: userInfo?.user?.phone?.mobile ?? '',
        home: userInfo?.user?.phone.home ?? '',
        firstName: userInfo?.user?.first_name ?? '',
        lastName: userInfo?.user?.last_name ?? '',
      });
    }
  }, [userInfo]);

  const cancelButtonRef = useRef(null);

  const getStoreCredits = async () => {
    try {
      const response = await instance({
        url: '/api/v1/patient/storecredits',
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
        method: 'GET',
      });

      const storeCredits = response.data?.balance ?? 0;

      setStoreCredits(storeCredits);

      return true;
    } catch (err) {
      console.error(err);
      showToast({
        message: 'Failed to get store credits',
        type: 'error',
      });
      return false;
    }
  };

  const { isFetching: isCreditsLoading } = useQuery({
    queryKey: ['store-credits'],
    queryFn: () => getStoreCredits(),
  });

  useEffect(() => {
    if (!open) {
      setReason('');
    }
  }, [open]);

  useEffect(() => {
    if (!loading) {
      setLoadingEnable(false);
    } else setLoadingEnable(true);
  }, [loading]);

  useEffect(() => {
    if (!user?.access_token) return;
    setUserLoading(true);

    getUserInfo(user?.access_token).then((data): void => {
      if (typeof data === 'number') {
        showToast({
          message: 'Unable to fetch user info',
          type: 'info',
        });
        setUserLoading(false);
        return;
      }
      setUserInfo(data);
      setEmergencyContact({
        id: data.emergency_contacts?.[0]?.id,
        contact_name: data.emergency_contacts?.[0]?.contact_name,
        contact_number: data.emergency_contacts?.[0]?.contact_number,
        contact_relationship:
          data.emergency_contacts?.[0]?.contact_relationship,
        consent: data.emergency_contacts?.[0]?.consent,
      });
      setNotificationStatus(data?.user?.isNotificationEnabled);
      setPushNotificationStatus(data?.user?.isPushNotificationEnabled);
      setUserLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerUserDataFetch]);

  useEffect(() => {
    if (
      (isNotificationsEnabled === userInfo?.user?.isNotificationEnabled &&
        isPushNotificationsEnabled ===
          userInfo?.user?.isPushNotificationEnabled) ||
      !user?.access_token
    )
      return;

    manageNotification(user.access_token, {
      email: userInfo?.user.email as string,
      isNotificationEnabled: isNotificationsEnabled as boolean,
      isPushNotificationEnabled: isPushNotificationsEnabled as boolean,
    }).then((status) => {
      if (status === 200) {
        const currentType =
          notificationType === NotificationOptions.PUSH
            ? isPushNotificationsEnabled
            : isNotificationsEnabled;
        showToast({
          message: `Notifications successfully ${currentType ? 'enabled' : 'disabled'}`,
          type: 'info',
        });
        setTrigger((p) => !p);
      } else {
        showToast({
          message: 'Unable to change notification status',
          type: 'error',
        });
        if (notificationType === NotificationOptions.PUSH) {
          setPushNotificationStatus((p) => p);
          return;
        }
        setNotificationStatus((p) => p);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotificationsEnabled, isPushNotificationsEnabled]);

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    setEmergencyContact({
      ...emergencyContacts,
      [e.target.name]: e.target.value,
    } as Emergencycontact);
  };

  const updatePatientDetails = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();

    const dataToUpdate = {
      emergency_contact_information: {
        ...emergencyContacts,
      },
      userId: userInfo?.user.id,
      patientId: userInfo?.id,
      note: userInfo?.note,
      userData: {
        internal: userInfo?.internal,
        home: userInfo?.user?.phone?.home,
        mobile: userInfo?.user?.phone?.mobile,
        firstName: userInfo?.user?.first_name,
        lastName: userInfo?.user?.last_name,
        email: userInfo?.user?.email,
      },
      needMail: true,
    };

    if (!dataToUpdate) return;

    const status = await updatePatientData(
      user?.access_token as string,
      dataToUpdate,
    );

    if (status === 200) {
      showToast({
        message: 'Patient info updated successfully.',
        type: 'success',
      });
    } else {
      showToast({
        message: 'Unable to update Patient info',
        type: 'info',
      });
    }
  };

  if (!user) return <p>User Profile Not Found</p>;

  const submitRequest = async () => {
    try {
      if (reason?.trim() === '') {
        showToast({
          message: 'Please provide reason for account deactivation',
          type: 'warning',
        });

        return;
      }

      const res = await instance.post(
        '/api/v1/request-for-deactivate',
        {
          reason: reason?.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${user.access_token}`,
          },
        },
      );

      if (res.status === 200) {
        showToast({
          message: res.data.message,
          type: 'success',
        });
      } else {
        showToast({
          message: res.data.message,
          type: 'warning',
        });
      }
      setOpen(false);
    } catch (err) {
      console.error(err);
      showToast({
        message: 'Failed to raise request from client',
        type: 'error',
      });
      setOpen(false);
    }
  };

  const WrapeNumberWithCountryCode = (number: string) => {
    if (number.length > 1) {
      return `+61 ${number}`;
    }
    return '-';
  };

  const updatePatientProfile = async () => {
    const response = await updatePatientData(user.access_token, {
      emergency_contact_information: {
        ...emergencyContacts,
      },
      userId: userInfo?.user.id,
      patientId: userInfo?.id,
      userData: {
        email: updateRecord.email,
        mobile: updateRecord.mobile,
        home: updateRecord.home,
        firstName: userInfo?.user?.first_name,
        lastName: userInfo?.user?.last_name,
        internal: userInfo?.internal,
      },
      needMail: true,
      note: userInfo?.note,
    });
    if (response === 200) {
      showToast({
        message: 'Patient record updated successfully.',
        type: 'success',
      });
    } else {
      showToast({
        message: 'Unable to update patient record.',
        type: 'error',
      });
    }

    setEditMailModal(false);
    refreshUserData();
  };

  const generatePasswordResetLink = async () => {
    try {
      setUserLoading(true);
      const response = await getPasswordResetUrl(user.access_token);
      if (typeof response === 'number') {
        showToast({
          message: 'Error while get Password reset link',
          type: 'warning',
        });
        return;
      }
      window.location.replace(response);
    } catch (err) {
      console.log('unable to get password reset link');
    } finally {
      setUserLoading(false);
    }
  };

  if (loading || !userInfo) return <Loading />;

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          initialFocus={cancelButtonRef}
          onClose={setOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel
                  className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4
                 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6"
                >
                  <div className="sm:flex sm:items-start">
                    <div
                      className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full
                     bg-red-100 sm:mx-0 sm:h-10 sm:w-10"
                    >
                      <ExclamationTriangleIcon
                        className="h-6 w-6 text-red-600"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900"
                      >
                        Deactivate account
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Are you sure you’d like to deactivate your account?
                          Once it’s deactivated, your details and history will
                          no longer be available. Please note this can’t be
                          reversed.
                        </p>
                      </div>
                      <div className="mt-2">
                        <input
                          type="text"
                          name="reason"
                          id="reason"
                          autoComplete="reason"
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1
                           ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset
                            focus:ring-indigo-600 sm:text-sm sm:leading-6"
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          placeholder="Reason"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:ml-10 sm:mt-4 sm:flex sm:pl-4">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm
                       font-semibold text-white shadow-sm hover:bg-red-500 sm:w-auto"
                      onClick={() => {
                        submitRequest();
                      }}
                    >
                      Request to deactivate
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm
                       font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:ml-3 sm:mt-0 sm:w-auto"
                      onClick={() => setOpen(false)}
                      ref={cancelButtonRef}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      <h2 className="text-3xl font-semibold text-primary-blue-500">
        hello,{' '}
        <span className="text-secondary-pink-600">
          {userInfo?.user?.first_name ?? ''}!
        </span>
      </h2>
      <div className="flex justify-between">
        <p className="text-2xl text-primary-blue-500 font-regular">
          It’s nice to see you again.
        </p>
        <div className="hidden md:block">
          <button
            className="w-11/14 bg-secondary-cornflowerblue-600 border border-secondary-cornflowerblue-600 py-3 px-2
              rounded-md text-white text-sm font-semibold 
              hover:text-secondary-cornflowerblue-600 hover:bg-white"
            onClick={() => generatePasswordResetLink()}
          >
            Reset Password
          </button>
        </div>
      </div>
      <div className="px-5 xl:px-16 py-12 bg-white rounded-md shadow mt-8">
        <div className="lg:ml-6">
          <ProfileDisplay
            intial={
              userInfo?.user?.first_name.charAt(0).toUpperCase() +
              userInfo?.user?.last_name?.charAt(0).toUpperCase()
            }
            name={''}
            isNotificationEnabled={isNotificationsEnabled as boolean}
            isPushNotificationEnabled={isPushNotificationsEnabled as boolean}
            toggleHandler={(val) => setNotificationStatus(val)}
            togglePushHandler={(val) => setPushNotificationStatus(val)}
            notificationType={(val) => setNotificationType(val)}
          />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mt-5 xl:mt-8">
          <div className="col-span-1 lg:col-span-3 bg-secondary-pink-50 px-6 xl:px-10 py-4 xl:py-7 rounded-md">
            <h3 className="mb-5 text-2xl text-primary-blue-500 font-bold">
              personal details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:gap-4">
              <div className="mb-6 xl:mb-8">
                <label className="text-lg xl:text-xl text-secondary-pink-700 font-bold">
                  Full name
                </label>
                <p className="text-sm xl:text-base text-primary-blue-500 font-regular">
                  {userInfo?.user?.first_name ?? ''}{' '}
                  {userInfo?.user?.last_name ?? ''}
                </p>
              </div>
              <div className="mb-6 xl:mb-8">
                <label className="text-lg xl:text-xl text-secondary-pink-700 font-bold">
                  Preferred name
                </label>
                <p className="text-sm xl:text-base text-primary-blue-500 font-regular">
                  {userInfo?.user?.preferred_name ?? ''}
                </p>
              </div>
              <div className="mb-6 xl:mb-8">
                <label className="text-lg xl:text-xl text-secondary-pink-700 font-bold">
                  DOB
                </label>
                <p className="text-sm xl:text-base text-primary-blue-500 font-regular">
                  {userInfo?.user?.dob
                    ? moment.utc(userInfo.user.dob).format('DD/MM/YYYY')
                    : '-'}
                </p>
              </div>
              <div className="mb-6 xl:mb-8">
                <label className="text-lg xl:text-xl text-secondary-pink-700 font-bold">
                  Gender
                </label>
                <p className="text-sm xl:text-base text-primary-blue-500 font-regular">
                  {userInfo?.user?.gender ?? '-'}
                </p>
              </div>
              <div className="mb-6 xl:mb-8">
                <label className="text-lg xl:text-xl text-secondary-pink-700 font-bold">
                  Medicare No.
                </label>
                <p className="text-sm xl:text-base text-primary-blue-500 font-regular">
                  {formatMedicareNumber(
                    userInfo.medicare,
                    userInfo.reference_id,
                  )}
                </p>
              </div>
              <div className="mb-6 xl:mb-8">
                <label className="text-lg xl:text-xl text-secondary-pink-700 font-bold">
                  Medicare Expiry
                </label>
                <p className="text-sm xl:text-base text-primary-blue-500 font-regular">
                  {userInfo.medicareExpiry
                    ? moment(userInfo.medicareExpiry).format('DD/MM/YYYY')
                    : '-'}
                </p>
              </div>
              <div>
                <label className="text-lg xl:text-xl text-secondary-pink-700 font-bold">
                  DVA No.
                </label>
                <p className="text-sm xl:text-base text-primary-blue-500 font-regular">
                  {userInfo?.dva ?? '-'}
                </p>
              </div>
            </div>
          </div>
          <div className="col-span-1 lg:col-span-2 flex justify-between flex-col">
            <div className="bg-secondary-tangerine-50 px-6 xl:px-10 py-4 xl:py-7 rounded-md h-full">
              <div className="flex justify-between">
                <h3 className="text-base md-large:text-xl text-primary-blue-500 font-semibold mb-5">
                  contact info
                </h3>
                <span
                  className="ml-2 cursor-pointer"
                  onClick={() => {
                    setUpdateRecord({
                      email: userInfo?.user?.email.toLowerCase() ?? '',
                      mobile: userInfo?.user?.phone?.mobile ?? '',
                      home: userInfo?.user?.phone?.home ?? '',
                      firstName: userInfo?.user?.first_name ?? '',
                      lastName: userInfo?.user?.last_name ?? '',
                    });
                    setEditMailModal(true);
                  }}
                >
                  <EditIcon className="w-5 h-5" />
                </span>
              </div>
              <div className="flex mb-6 xl:mb-8 items-center justify-between relative">
                <div className="flex">
                  <div>
                    <ProfileEmailIcon className="mr-5" />
                  </div>
                  <p className="text-sm xl:text-base text-primary-blue-500 font-regular flex items-center relative">
                    <WrapEmail email={userInfo?.user?.email ?? '-'} />
                    {!userDetail.supportingParams?.hasValidEmail && (
                      <p className="relative">
                        <span
                          onMouseEnter={() => setMailAlert(true)}
                          onMouseLeave={() => setMailAlert(false)}
                        >
                          <AlertIcon className="ml-2 fill-primary-red-400 w-5 h-5 cursor-pointer" />
                        </span>
                        {mailAlert && (
                          <Tooltip
                            text="Your email is invalid. Please update your email address. 
                          Note, this will also change your login username."
                            className="z-50 top-0 right-0 !w-[250px] xl:!w-[350px]"
                          />
                        )}
                      </p>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex mb-6 xl:mb-8 items-center">
                <div>
                  <ProfileMobileIcon className="mr-5" />
                </div>
                <p className="text-sm xl:text-basetext-base text-primary-blue-500 font-regular">
                  {WrapeNumberWithCountryCode(
                    formatPhoneNumber({
                      phoneNumber: userInfo?.user?.phone?.mobile ?? '',
                    }) ?? '-',
                  )}
                </p>
              </div>
              <div className="flex mb-6 xl:mb-8 items-center">
                <div>
                  <ProfileTelePhoneIcon className="mr-5" />
                </div>
                <p className="text-sm xl:text-basetext-base text-primary-blue-500 font-regular">
                  {WrapeNumberWithCountryCode(
                    formatPhoneNumber({
                      phoneNumber: userInfo?.user?.phone?.home ?? '',
                    }) ?? '-',
                  )}
                </p>
              </div>
              <div className="flex items-center">
                <div>
                  <ProfileLocationIcon className="mr-5" />
                </div>
                <p className="text-sm xl:text-basetext-base text-primary-blue-500 font-regular">
                  {userInfo?.address?.street_number ?? ''}{' '}
                  {userInfo?.address?.street_name ?? ''}{' '}
                  {userInfo?.address?.suburb ?? ''}{' '}
                  {userInfo?.address?.state?.name ?? ''} Australia{' '}
                  {userInfo?.address?.post_code ?? ''}
                </p>
              </div>
            </div>
            {credits !== 0 && (
              <div className="bg-secondary-tangerine-50 px-6 xl:px-10 py-2 xl:py-5 rounded-md mt-5">
                <h3 className="text-xl text-primary-blue-500 font-semibold">
                  credit balance
                </h3>
                <div className="mt-4 flex items-center">
                  <WalletIcon />
                  {isCreditsLoading ? (
                    <SkeletonLoader className="ml-3 w-10 h-7" />
                  ) : (
                    <span className="ml-3 text-lg font-regular text-primary-blue-500">
                      ${credits}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="bg-secondary-cornflowerblue-50 px-6 xl:px-10 py-4 xl:py-7 rounded-md mt-4">
          <div className="flex justify-between items-baseline">
            <h3 className="text-base md-large:text-xl text-primary-blue-500 font-semibold mb-5">
              Consent to Contact
            </h3>

            {editable ? (
              <div className="flex items-center">
                <button
                  onClick={() => {
                    setEditable(false);
                    setTrigger((p) => !p);
                  }}
                  className="mr-3 md-large:mr-6 text-secondary-darkred-500 text-sm font-semibold bg-secondary-darkred-500 md-large:bg-transparent rounded-full md-large:rounded-md w-[30px] h-[30px] md-large:w-auto md-large:h-auto md-large:px-5 md-large:py-3 flex justify-center items-center"
                >
                  <span className="block md-large:hidden">
                    <MobileCancelIcon className="w-5" />
                  </span>
                  <span className="hidden md-large:block">Cancel</span>
                </button>
                <button
                  onClick={(e) => {
                    updatePatientDetails(e);
                    setEditable(false);
                  }}
                  className="bg-secondary-green-600 text-white rounded-full md-large:rounded-md w-[30px] h-[30px] md-large:w-auto md-large:h-auto md-large:px-5 md-large:py-3 flex justify-center items-center text-sm"
                >
                  <span className="block md-large:hidden">
                    <MobileSaveIcon className="w-5" />
                  </span>
                  <span className="hidden md-large:block">Save Changes</span>
                </button>
              </div>
            ) : (
              <span
                className="ml-2 cursor-pointer"
                onClick={() => setEditable(true)}
              >
                <EditIcon className="w-5 h-5" />
              </span>
            )}
          </div>

          <div className="flex flex-col md-large:flex-row justify-between">
            <div className="mb-5 md-large:mb-0">
              <label
                htmlFor="contact-name"
                className="text-lg xl:text-xl text-secondary-pink-700 font-bold"
              >
                Contact Name
              </label>
              <input
                type="text"
                name="contact_name"
                id="contact-name"
                autoComplete="given-name"
                className={`w-11/12 text-base font-regular text-primary-blue-500 bg-secondary-cornflowerblue-50
                        ${editable ? 'border border-secondary-cornflowerblue-400 rounded-md' : 'border-0 p-0'}`}
                value={emergencyContacts?.contact_name ?? '-'}
                onChange={(e) => handleInput(e)}
                readOnly={!editable}
              />
            </div>
            <div className="mb-5 md-large:mb-0">
              <label
                htmlFor="contact-phone"
                className="text-lg xl:text-xl text-secondary-pink-700 font-bold"
              >
                Contact Phone
              </label>
              <input
                type="number"
                name="contact_number"
                id="conatact-phone"
                className={`w-11/12 placeholder:text-gray-400 text-base font-regular text-primary-blue-500 bg-secondary-cornflowerblue-50
                             ${editable ? 'border border-secondary-cornflowerblue-400 rounded-md' : 'border-0 p-0'}`}
                onChange={(e) => handleInput(e)}
                value={emergencyContacts?.contact_number ?? '-'}
                readOnly={!editable}
              />
            </div>
            <div>
              <label
                htmlFor="contact-relationship"
                className="text-lg xl:text-xl text-secondary-pink-700 font-bold"
              >
                Relationship to Patient
              </label>
              <input
                type="text"
                name="contact_relationship"
                id="contact-relationship"
                autoComplete="given-name"
                className={`w-11/12 text-base font-regular text-primary-blue-500 bg-secondary-cornflowerblue-50
                        ${editable ? 'border border-secondary-cornflowerblue-400 rounded-md' : 'border-0 p-0'}`}
                value={emergencyContacts?.contact_relationship ?? '-'}
                onChange={(e) => handleInput(e)}
                readOnly={!editable}
              />
            </div>
          </div>
          <div className="mt-5">
            <label className="flex items-center space-x-3 mb-2">
              <input
                id="comments"
                aria-describedby="comments-description"
                name="consent"
                type="checkbox"
                className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 
                      ${editable ? '' : 'opacity-50 cursor-not-allowed'}`}
                checked={!!emergencyContacts?.consent}
                onChange={() => {
                  if (editable) {
                    setEmergencyContact({
                      ...emergencyContacts,
                      consent: !emergencyContacts?.consent,
                    } as Emergencycontact);
                  }
                }}
              />
              <span className="text-xs text-primary-blue-500 font-regular">
                I give consent for my emergency contact to speak on my behalf.
              </span>
            </label>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mt-5 xl:mt-8 items-center">
          <div className="col-span-1 lg:col-span-3">
            <p className="text-sm lg:text-lg font-regular text-primary-blue-500 mt-5 lg:mt-0 self-start">
              Need help updating your details? Email{' '}
              <a
                href="mailTo:hello@monivearoad.com.au"
                className="text-secondary-pink-600"
              >
                hello@monivearoad.com.au{' '}
              </a>
              or call{' '}
              <a className="text-secondary-pink-600" href="tel:1800123687">
                1800 123 687
              </a>
              .
            </p>
          </div>
          {(user.profile.groups as string[]).includes(ROLE.Patients) && (
            <div className="col-span-1 lg:col-span-2 flex justify-center md:justify-end">
              <div className="mt-3">
                <button
                  className="font-semibold text-sm text-primary-red-400 inline-flex border-2 rounded-md px-6 py-3 border-primary-red-400"
                  onClick={() => setOpen(true)}
                >
                  <DeactivateIcon />{' '}
                  <span className="ml-4">Deactivate Account</span>
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-center block md:hidden mt-5">
          <button
            className="w-full max-w-[230px] bg-secondary-cornflowerblue-600 border border-secondary-cornflowerblue-600 py-3 px-2
              rounded-md text-white text-sm font-semibold 
              hover:text-secondary-cornflowerblue-600 hover:bg-white"
            onClick={() => generatePasswordResetLink()}
          >
            Reset Password
          </button>
        </div>
      </div>
      <CustomModal
        isOpen={editMailModal}
        onClose={() => setEditMailModal(false)}
        header="Update Profile"
        modalSize="w-10/12 md:w-1/2 xl:w-1/4"
        footer={
          <>
            <button
              className="bg-white border border-solid border-primary-blue-500 text-primary-blue-500 w-24 px-4 py-2 rounded"
              onClick={() => setEditMailModal(false)}
            >
              Cancel
            </button>
            <button
              className={`ml-4 bg-primary-blue-500 text-white px-4 py-2 w-24 rounded ${isDisabled ? 'opacity-50' : ''}`}
              onClick={updatePatientProfile}
              disabled={isDisabled}
            >
              Save
            </button>
          </>
        }
      >
        <div className="flex flex-col">
          <label className="text-base text-primary-blue-500">Email</label>
          <input
            type="text"
            className="w-full border-0.5 border-primary-blue-500 rounded p-2"
            value={updateRecord.email}
            onChange={(e) => {
              const typedEmail = e.target.value.toLowerCase();
              setUpdateRecord((p) => ({ ...p, email: typedEmail }));
              if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(typedEmail)) {
                setEmailValidationMsg('Invalid email format');
              } else {
                setEmailValidationMsg('');
              }

              setDisabled(
                !typedEmail ||
                  typedEmail === userInfo.user.email.toLowerCase() ||
                  emailValidationMsg !== '',
              );
            }}
          />
          <span className="text-primary-red-500 text-xs mt-1">
            {emailValidationMsg}
          </span>
        </div>
        <div className="flex flex-col mt-3">
          <label className="text-base text-primary-blue-500">Mobile</label>
          <input
            type="number"
            className="w-full border-0.5 border-primary-blue-500 rounded p-2"
            value={formatPhoneNumber({ phoneNumber: updateRecord.mobile })}
            onChange={(e) => {
              const typedMobile = e.target.value.toLowerCase();
              setUpdateRecord((p) => ({ ...p, mobile: typedMobile }));
              setDisabled(typedMobile === userInfo.user.phone.mobile);
            }}
            placeholder="92851538368"
          />
        </div>
        <div className="flex flex-col mt-3">
          <label className="text-base text-primary-blue-500">Home</label>
          <input
            type="number"
            className="w-full border-0.5 border-primary-blue-500 rounded p-2"
            value={formatPhoneNumber({ phoneNumber: updateRecord.home })}
            onChange={(e) => {
              const typedHome = e.target.value;
              setUpdateRecord((p) => ({ ...p, home: typedHome }));

              setDisabled(typedHome === userInfo.user.phone.home);
            }}
            placeholder="255551212"
          />
        </div>
      </CustomModal>
    </>
  );
};

export default ProfileComponent;
